import React from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Plus, Trash2, Edit, Save, X, Music, Youtube, Instagram, Facebook, Globe } from 'lucide-react';

const platformIcons = {
  soundcloud: Music, bandcamp: Music, youtube: Youtube,
  instagram: Instagram, facebook: Facebook, spotify: Music, other: Globe
};

const SocialItem = ({ social, isEditing, editingSocial, setEditingSocial, onUpdate, onDelete }) => {
  const Icon = platformIcons[social.platform.toLowerCase()] || Globe;

  return (
    <div className="flex items-center justify-between bg-black/30 p-4 rounded-lg border border-green-500/20">
      <div className="flex items-center space-x-4 flex-1">
        <Icon className="w-5 h-5 text-green-400" />
        {isEditing ? (
          <div className="flex-1 space-y-2">
            <Input
              value={editingSocial.platform}
              onChange={(e) => setEditingSocial({ ...editingSocial, platform: e.target.value })}
              placeholder="Platform (e.g., soundcloud)"
              className="bg-black/50 border-green-500/30"
            />
            <Input
              value={editingSocial.url}
              onChange={(e) => setEditingSocial({ ...editingSocial, url: e.target.value })}
              placeholder="URL"
              className="bg-black/50 border-green-500/30"
            />
          </div>
        ) : (
          <div className="flex-1">
            <p className="text-white font-semibold capitalize">{social.platform}</p>
            <p className="text-gray-400 text-sm truncate">{social.url}</p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {isEditing ? (
          <>
            <Button onClick={() => onUpdate(social.id, editingSocial)} size="sm" className="bg-green-500 hover:bg-green-600">
              <Save className="w-4 h-4" />
            </Button>
            <Button onClick={() => setEditingSocial(null)} size="sm" variant="outline">
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setEditingSocial(social)} size="sm" className="bg-blue-500 hover:bg-blue-600">
              <Edit className="w-4 h-4" />
            </Button>
            <Button onClick={() => onDelete(social)} size="sm" variant="destructive">
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export const SocialMediaSection = ({
  socialMedia, editingSocial, setEditingSocial,
  isAddingSocial, setIsAddingSocial,
  newSocial, setNewSocial,
  onAdd, onUpdate, onDelete
}) => {
  return (
    <>
      <Card className="bg-black/50 border-green-500/30 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-green-400">Social Media Links</h3>
          <Button onClick={() => setIsAddingSocial(true)} className="bg-green-500 hover:bg-green-600">
            <Plus className="w-4 h-4 mr-2" /> Add Link
          </Button>
        </div>

        <div className="space-y-3">
          {socialMedia.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No social media links added yet</p>
          ) : (
            socialMedia.map((social) => (
              <SocialItem
                key={social.id}
                social={social}
                isEditing={editingSocial?.id === social.id}
                editingSocial={editingSocial}
                setEditingSocial={setEditingSocial}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))
          )}
        </div>
      </Card>

      {/* Add Social Dialog */}
      <Dialog open={isAddingSocial} onOpenChange={setIsAddingSocial}>
        <DialogContent className="bg-black border-green-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-400">Add Social Media Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Platform</label>
              <select
                value={newSocial.platform}
                onChange={(e) => setNewSocial({ ...newSocial, platform: e.target.value })}
                className="w-full bg-black/50 border border-green-500/30 rounded-md px-3 py-2 text-white"
              >
                <option value="">Select Platform</option>
                <option value="soundcloud">SoundCloud</option>
                <option value="bandcamp">Bandcamp</option>
                <option value="youtube">YouTube</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="spotify">Spotify</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">URL</label>
              <Input
                value={newSocial.url}
                onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                placeholder="https://..."
                className="bg-black/50 border-green-500/30"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Order</label>
              <Input
                type="number"
                value={newSocial.order}
                onChange={(e) => setNewSocial({ ...newSocial, order: parseInt(e.target.value) })}
                className="bg-black/50 border-green-500/30"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={() => setIsAddingSocial(false)} variant="outline">Cancel</Button>
              <Button onClick={onAdd} className="bg-green-500 hover:bg-green-600" disabled={!newSocial.platform || !newSocial.url}>
                Add Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
